/**
 * Generic utilities for CSV file import column-mapping flows.
 *
 * Currently consumed by wireImport/helpers.ts. The ACH import flow
 * (achImport/helpers.ts) has its own inline implementations that could
 * be migrated here in a future refactor.
 *
 * These utilities work for any field union using the
 * `{field}_column_index` and `{field}_column_header` naming conventions.
 */

/**
 * Safe accessor for preview row data.
 * Returns "" for out-of-bounds indices.
 */
export const getPreviewValue = (
  previewRows: string[][],
  columnIndex: number,
  previewRowIndex: number,
): string => {
  const row = previewRows[previewRowIndex];
  if (!row) return "";
  return row[columnIndex] ?? "";
};

/**
 * Generic mapping conversion from flat persisted mapping to ColumnMapping[].
 *
 * Iterates over the given fields and extracts `{field}_column_index` and
 * `{field}_column_header` from the mapping object. Fields with a null index
 * are skipped.
 */
export const flatMappingToColumnMappings = <
  F extends string,
  M extends Record<string, unknown>,
>(
  mapping: M,
  fields: F[],
): { field: F; column_index: number; column_header: string | null }[] => {
  return fields.reduce<
    { field: F; column_index: number; column_header: string | null }[]
  >((acc, field) => {
    const index = mapping[`${field}_column_index`];
    if (typeof index === "number") {
      const header = mapping[`${field}_column_header`];
      acc.push({
        field,
        column_index: index,
        column_header: typeof header === "string" ? header : null,
      });
    }
    return acc;
  }, []);
};

/**
 * Generic inverse conversion from ColumnMapping[] to flat request object.
 *
 * Takes a template (with all fields initialized to null) and populates it
 * with column_index / column_header values from the mappings array.
 */
export const columnMappingsToFlatMapping = <
  F extends string,
  R extends Record<string, unknown>,
>(
  template: R,
  columnMappings: {
    field: F;
    column_index: number;
    column_header: string | null;
  }[],
): R => {
  const result = { ...template };
  columnMappings.forEach((cm) => {
    const indexKey = `${cm.field}_column_index`;
    const headerKey = `${cm.field}_column_header`;
    (result as Record<string, unknown>)[indexKey] = cm.column_index;
    (result as Record<string, unknown>)[headerKey] = cm.column_header;
  });
  return result;
};
